import React, { useMemo } from 'react';
import Link from 'next/link';
import ArrowDownSLineIcon from 'remixicon-react/ArrowDownSLineIcon';
import { formatNumber } from '@/utils/coins/formatNumber';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { generateAvatarFallback } from '@/utils/generateAvatarFallback';

interface CoinData {
    price: {
        buyAmount: string;
        buyAmountToman: string;
        buyPrice: string;
        buyPriceToman: string;
        sellAmount: string;
        sellAmountToman: string;
        sellPrice: string;
        sellPriceToman: string;
    };
    symbol: string;
    iconUrl: string;
    nameFa?: string;
}

interface UseColumnsProps {
    currency: 'USDT' | 'TMN' | string; // Assuming these are the only valid values
    selectedSort: string;
    dataToPass: CoinData[] | undefined; // This is now secondary data
    registerLink?: string;
    coins?: CoinData[]; // This is now the primary source for data
    handleSortChange: (columnId: string) => void;
    currentPage: number; // Add currentPage as a prop here
}

function formatCurrency(amount: number, currency: 'USDT' | 'TMN' | string): string {
    if (currency === 'USDT') {
        return `${formatNumber(amount, false, true, 'USD', true)} دلار`;
    } else {
        return `${formatNumber(amount, false, true, 'TMN', true)} تومان`;
    }
}

export const useColumns = ({
    currency,
    selectedSort,
    dataToPass,
    registerLink,
    handleSortChange,
}: UseColumnsProps) =>
    useMemo(() => {
        if (!dataToPass || !dataToPass.length) {
            return [];
        }

        // Extract priceBuy and priceSell values based on currency
        const priceBuyValues = dataToPass
            .map((row) =>
                currency === 'USDT'
                    ? parseFloat(row?.price?.buyPrice)
                    : parseFloat(row?.price?.buyPriceToman),
            )
            .filter((value) => !isNaN(value));

        const priceSellValues = dataToPass
            .map((row) =>
                currency === 'USDT'
                    ? parseFloat(row?.price?.sellPrice)
                    : parseFloat(row?.price?.sellPriceToman),
            )
            .filter((value) => !isNaN(value));

        const maxPriceBuy = Math.max(...priceBuyValues);
        const minPriceSell = Math.min(...priceSellValues);

        return [
            {
                accessorKey: 'name',
                header: () => {
                    return <div className={'text-center font-light'}>ارز دیجیتال</div>;
                },
                cell: ({ row }: { row: any }) => {
                    const name = row.original.symbol;
                    const path = `symbol/${name.toLowerCase()}`; // The dynamic path part
                    return (
                        <Link
                            rel={'nofollow'}
                            href={`/${path || '#'}`}
                            className={'inline-flex items-center gap-x-2'}
                        >
                            <Avatar className={'flex justify-center items-center'}>
                                <AvatarImage
                                    className={'object-cover size-6'}
                                    src={row?.original.iconUrl || ''}
                                    alt={row?.original.symbol}
                                    title={row?.original.symbol}
                                />
                                <AvatarFallback
                                    className={
                                        'text-text/50 dark:text-white bg-primary/10 dark:bg-white/10 size-6 text-xxs'
                                    }
                                >
                                    {generateAvatarFallback(row?.original.symbol)}
                                </AvatarFallback>
                            </Avatar>
                            <div className={'flex flex-col'}>
                                <span className={'hover:underline inline-flex items-center gap-2'}>
                                    {name}
                                </span>
                                <span>{row?.original.nameFa}</span>
                            </div>
                        </Link>
                    );
                },
            },
            {
                id: 'priceBuyUSDT',
                accessorFn: (row: any) =>
                    parseFloat(
                        currency === 'USDT'
                            ? row.original?.price?.buyPrice
                            : row.original?.price?.buyPriceToman,
                    ),
                header: ({ column }: { column: any }) => {
                    const sortDirection = column.getIsSorted();
                    return (
                        <div
                            className={`relative inline-flex gap-1 px-0 lg:!whitespace-normal cursor-pointer hover:text-primary dark:hover:text-white transition-all ${selectedSort === 'priceBuyUSDT' ? 'text-primary dark:text-white' : 'font-light text-primary/40 dark:text-white/40'}`}
                            onClick={() => handleSortChange('priceBuyUSDT')} // Add click handler
                        >
                            قیمت خرید
                            {selectedSort === 'priceBuyUSDT' && ( // Add sort indicator arrow logic
                                <div className={'size-4 absolute -left-4'}>
                                    <ArrowDownSLineIcon
                                        className={`size-4 transition-all ${sortDirection === 'asc' ? 'rotate-180' : ''}`}
                                    />
                                </div>
                            )}
                        </div>
                    );
                },
                cell: ({ row }: { row: any }) => {
                    const amount = parseFloat(
                        currency === 'USDT'
                            ? row.original.price.buyPrice
                            : row.original.price.buyPriceToman,
                    );
                    const isMax = amount === maxPriceBuy;

                    return (
                        <div className={`text-center ${isMax ? 'text-green-500' : ''}`}>
                            {formatCurrency(amount, currency)}
                        </div>
                    );
                },
            },
            {
                id: 'amountBuyUSDT',
                accessorFn: (row: any) => {
                    const amountStr =
                        currency === 'USDT'
                            ? row?.original?.price?.buyAmount
                            : row?.original?.price?.buyAmountToman;
                    const amount = parseFloat(amountStr);
                    return amount === 0 ? Infinity : amount; // Handle 'unlimited' for sorting
                },
                header: ({ column }: { column: any }) => {
                    const sortDirection = column.getIsSorted();
                    return (
                        <div
                            className={`relative inline-flex gap-1 px-0 whitespace-nowrap justify-center text-center cursor-pointer hover:text-primary dark:hover:text-white transition-all ${selectedSort === 'amountBuyUSDT' ? 'text-primary dark:text-white' : 'font-light text-primary/40 dark:text-white/40'}`}
                            onClick={() => handleSortChange('amountBuyUSDT')}
                        >
                            مقدار فروش
                            {selectedSort === 'amountBuyUSDT' && (
                                <div className={'size-4 absolute -left-4 '}>
                                    <ArrowDownSLineIcon
                                        className={`size-4 transition-all ${sortDirection === 'asc' ? 'rotate-180' : ''}`}
                                    />
                                </div>
                            )}
                        </div>
                    );
                },
                cell: ({ row }: { row: any }) => {
                    const buyAmount =
                        currency === 'USDT'
                            ? row?.original.price.buyAmount != 0
                                ? row?.original.price.buyAmount
                                : 'نامحدود'
                            : row?.original.price.buyAmountToman != 0
                              ? row?.original.price.buyAmountToman
                              : 'نامحدود';
                    return (
                        <div className='text-center'>
                            {buyAmount === '0' ? 'نامحدود' : buyAmount}
                        </div>
                    );
                },
            },
            {
                id: 'priceSellUSDT',
                accessorFn: (row: any) =>
                    parseFloat(
                        currency === 'USDT'
                            ? row?.original?.price?.sellPrice
                            : row?.original?.price?.sellPriceToman,
                    ),
                header: ({ column }: { column: any }) => {
                    const sortDirection = column.getIsSorted();
                    return (
                        <div
                            className={`relative inline-flex gap-1 px-0 lg:!whitespace-normal cursor-pointer hover:text-primary dark:hover:text-white transition-all ${selectedSort === 'priceSellUSDT' ? 'text-primary dark:text-white' : 'font-light text-primary/40 dark:text-white/40'}`}
                            onClick={() => handleSortChange('priceSellUSDT')}
                        >
                            قیمت فروش
                            {selectedSort === 'priceSellUSDT' && (
                                <div className={'size-4 absolute -left-4'}>
                                    <ArrowDownSLineIcon
                                        className={`size-4 transition-all ${sortDirection === 'asc' ? 'rotate-180' : ''}`}
                                    />
                                </div>
                            )}
                        </div>
                    );
                },
                cell: ({ row }: { row: any }) => {
                    const amount = parseFloat(
                        currency === 'USDT'
                            ? row.original.price.sellPrice
                            : row.original.price.sellPriceToman,
                    );
                    const isMin = amount === minPriceSell;

                    return (
                        <div className={`text-center ${isMin ? 'text-green-500 font-bold' : ''}`}>
                            {formatCurrency(amount, currency)}
                        </div>
                    );
                },
            },
            {
                id: 'amountSellUSDT',
                accessorFn: (row: any) => {
                    const amountStr =
                        currency === 'USDT'
                            ? row?.original?.price?.sellAmount
                            : row?.original?.price?.sellAmountToman;
                    const amount = parseFloat(amountStr);
                    return amount === 0 ? Infinity : amount; // Handle 'unlimited' for sorting
                },
                header: ({ column }: { column: any }) => {
                    const sortDirection = column.getIsSorted();
                    return (
                        <div
                            className={`relative inline-flex gap-1 px-0 whitespace-nowrap cursor-pointer hover:text-primary dark:hover:text-white transition-all ${selectedSort === 'amountSellUSDT' ? 'text-primary dark:text-white' : 'font-light text-primary/40 dark:text-white/40'}`}
                            onClick={() => handleSortChange('amountSellUSDT')}
                        >
                            مقدار فروش
                            {selectedSort === 'amountSellUSDT' && (
                                <div className={'size-4 absolute -left-4'}>
                                    <ArrowDownSLineIcon
                                        className={`size-4 transition-all ${sortDirection === 'asc' ? 'rotate-180' : ''}`}
                                    />
                                </div>
                            )}
                        </div>
                    );
                },
                cell: ({ row }: { row: any }) => {
                    const sellAmount =
                        currency === 'USDT'
                            ? row?.original.price.sellAmount != 0
                                ? row?.original.price.sellAmount
                                : 'نامحدود'
                            : row?.original.price.sellAmountToman != 0
                              ? row?.original.price.sellAmountToman
                              : 'نامحدود';
                    return (
                        <div className='text-center'>
                            {sellAmount === '0' ? 'نامحدود' : sellAmount}
                        </div>
                    );
                },
            },
            {
                id: 'marketCap',
                header: () => {
                    return <div className={'text-center font-light'}>معامله</div>;
                },
                cell: () => {
                    return (
                        <div
                            className={
                                'mx-auto flex w-full justify-center py-3 px-5 transition-all lg:w-10/12'
                            }
                        >
                            <Link
                                className={'inline-flex gap-1'}
                                href={registerLink || '#'}
                                rel={'nofollow'}
                                target={'_blank'}
                            >
                                <span className={'text-success'}>خرید</span> /{' '}
                                <span className={'text-red-600 dark:text-red-400'}>فروش</span>
                            </Link>
                        </div>
                    );
                },
            },
        ];
    }, [dataToPass, currency, selectedSort, handleSortChange, registerLink]);
